import React, { useState, useCallback } from 'react';
import { Modal } from '@/components/ui/modal';
import Button from '@/components/ui/button/Button';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import AddressAutocomplete from '@/components/form/input/AddressAutocomplete';
import { useTranslation } from 'react-i18next';
import { withFormProtection } from '@/components/form/withFormProtection';
import { useToast } from '@/components/ui/toast/Toast';

interface RentingAddressEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  initialData?: FormData;
  currentData?: FormData;
  onDataChange?: (data: FormData) => void;
  type: 'delivery' | 'pickup';
  currentAddress: string;
  currentLatitude: string;
  currentLongitude: string;
}

interface FormData {
  address: string;
  latitude: string;
  longitude: string;
}

const RentingAddressEditModal = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean; isValid: () => boolean }, RentingAddressEditModalProps>(({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  onDataChange,
  type,
  currentAddress,
  currentLatitude,
  currentLongitude,
}, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [address, setAddress] = useState(initialData?.address || currentAddress || '');
  const [latitude, setLatitude] = useState(initialData?.latitude || currentLatitude || '');
  const [longitude, setLongitude] = useState(initialData?.longitude || currentLongitude || '');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Обновляем состояние только при открытии модального окна
  React.useEffect(() => {
    if (isOpen) {
      const newAddress = initialData?.address || currentAddress || '';
      const newLatitude = initialData?.latitude || currentLatitude || '';
      const newLongitude = initialData?.longitude || currentLongitude || '';
      setAddress(newAddress);
      setLatitude(newLatitude);
      setLongitude(newLongitude);
    }
  }, [isOpen]);

  // Notify parent about form data changes
  const handleFormDataChange = useCallback((newAddress: string, newLatitude: string, newLongitude: string) => {
    onDataChange?.({
      address: newAddress,
      latitude: newLatitude,
      longitude: newLongitude,
    });
  }, [onDataChange]);

  // Update form data when address changes
  const handleAddressChange = (value: string) => {
    setAddress(value);
    handleFormDataChange(value, latitude, longitude);
    validateForm(value, latitude, longitude);
  };

  // Handle place selection from Google Autocomplete
  const handlePlaceSelect = (place: any) => {
    const address = place.formatted_address || place.name || '';
    const lat = place.geometry?.location?.lat?.() || '';
    const lng = place.geometry?.location?.lng?.() || '';

    setAddress(address);
    setLatitude(lat.toString());
    setLongitude(lng.toString());
    handleFormDataChange(address, lat.toString(), lng.toString());
    validateForm(address, lat.toString(), lng.toString());
  };

  // Update form data when latitude changes
  const handleLatitudeChange = (value: string) => {
    setLatitude(value);
    handleFormDataChange(address, value, longitude);
    validateForm(address, value, longitude);
  };

  // Update form data when longitude changes
  const handleLongitudeChange = (value: string) => {
    setLongitude(value);
    handleFormDataChange(address, latitude, value);
    validateForm(address, latitude, value);
  };

  const validateForm = (currentAddress: string, currentLatitude: string, currentLongitude: string): boolean => {
    if (!currentAddress.trim()) {
      setError(t('rentings.address_edit.errors.address_required'));
      return false;
    }
    if (!currentLatitude.trim()) {
      setError(t('rentings.address_edit.errors.latitude_required'));
      return false;
    }
    if (!currentLongitude.trim()) {
      setError(t('rentings.address_edit.errors.longitude_required'));
      return false;
    }

    // Validate latitude format (should be between -90 and 90)
    const latNum = parseFloat(currentLatitude);
    if (isNaN(latNum) || latNum < -90 || latNum > 90) {
      setError(t('rentings.address_edit.errors.latitude_invalid'));
      return false;
    }

    // Validate longitude format (should be between -180 and 180)
    const lngNum = parseFloat(currentLongitude);
    if (isNaN(lngNum) || lngNum < -180 || lngNum > 180) {
      setError(t('rentings.address_edit.errors.longitude_invalid'));
      return false;
    }

    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm(address, latitude, longitude)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        address: address.trim(),
        latitude: latitude.trim(),
        longitude: longitude.trim(),
      });
      onClose();
      toast.success(t('rentings.address_edit.update_success'));
    } catch (error) {
      toast.error(t('rentings.address_edit.update_error' + `<br/><br/>${error}`));
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = type === 'delivery'
    ? t('rentings.address_edit.title.edit_delivery_address')
    : t('rentings.address_edit.title.edit_pickup_address');

  // Проверка валидности формы
  const isValid = useCallback(() => {
    return validateForm(address, latitude, longitude);
  }, [address, latitude, longitude]);

  // Forward the ref
  React.useImperativeHandle(ref, () => ({
    handleClose: onClose,
    isDirty: () => {
      if (!initialData) return false;
      const normalizeData = (data: any) => {
        const normalized = { ...data };
        Object.keys(normalized).forEach(key => {
          if (normalized[key] === undefined || normalized[key] === null) {
            normalized[key] = '';
          }
          if (typeof normalized[key] === 'string') {
            normalized[key] = normalized[key].trim();
          }
        });
        return normalized;
      };
      const normalizedInitial = normalizeData(initialData);
      const normalizedCurrent = normalizeData({ address, latitude, longitude });
      return JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedCurrent);
    },
    isValid,
  }), [onClose, initialData, address, latitude, longitude, isValid]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        className="max-w-[500px] p-6"
      >
        <div className="px-6 py-4">
          <h5 className="text-xl font-semibold text-gray-800 dark:text-white/90 lg:text-2xl">
            {title}
          </h5>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('rentings.address_edit.description')}
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <div>
              <Label>
                {t('rentings.address_edit.fields.address')}
                <span className="text-danger ml-1">*</span>
              </Label>
              <AddressAutocomplete
                value={address}
                onChange={handleAddressChange}
                onPlaceSelect={handlePlaceSelect}
                error={error}
                placeholder={t('rentings.address_edit.placeholders.enter_address')}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>
                  {t('rentings.address_edit.fields.latitude')}
                  <span className="text-danger ml-1">*</span>
                </Label>
                <Input
                  type="number"
                  step="any"
                  value={latitude}
                  onChange={(e) => handleLatitudeChange(e.target.value)}
                  placeholder={t('rentings.address_edit.placeholders.enter_latitude')}
                  error={!!error}
                  aria-required="true"
                />
              </div>

              <div>
                <Label>
                  {t('rentings.address_edit.fields.longitude')}
                  <span className="text-danger ml-1">*</span>
                </Label>
                <Input
                  type="number"
                  step="any"
                  value={longitude}
                  onChange={(e) => handleLongitudeChange(e.target.value)}
                  placeholder={t('rentings.address_edit.placeholders.enter_longitude')}
                  error={!!error}
                  aria-required="true"
                />
              </div>
            </div>

            {error && (
              <div className="text-danger text-sm">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="flex items-center gap-3 sm:justify-end">
            <Button variant="outline" size="sm" onClick={onClose}>
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('rentings.address_edit.buttons.updating') : t('rentings.address_edit.buttons.update')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
});

RentingAddressEditModal.displayName = 'RentingAddressEditModal';

const ProtectedRentingAddressEditModal = withFormProtection(RentingAddressEditModal);

interface RentingAddressEditModalWrapperProps extends Omit<RentingAddressEditModalProps, 'currentData' | 'onDataChange' | 'initialData'> {
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean; isValid: () => boolean }>;
}

const RentingAddressEditModalWrapper = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean; isValid: () => boolean }, RentingAddressEditModalWrapperProps>((props, ref) => {
  const initialData = {
    address: props.currentAddress || '',
    latitude: props.currentLatitude || '',
    longitude: props.currentLongitude || '',
  };

  const [currentData, setCurrentData] = useState<FormData>(initialData);

  // Обновляем currentData только при открытии модального окна (смене аренды)
  React.useEffect(() => {
    if (props.isOpen) {
      const newData = {
        address: props.currentAddress || '',
        latitude: props.currentLatitude || '',
        longitude: props.currentLongitude || '',
      };
      setCurrentData(newData);
    }
  }, [props.isOpen, props.currentAddress, props.currentLatitude, props.currentLongitude]);

  const handleDataChange = useCallback((data: FormData) => {
    setCurrentData(data);
  }, []);

  return (
    <ProtectedRentingAddressEditModal
      {...props}
      ref={ref}
      initialData={initialData}
      currentData={currentData}
      onDataChange={handleDataChange}
    />
  );
});

RentingAddressEditModalWrapper.displayName = 'RentingAddressEditModalWrapper';

export default RentingAddressEditModalWrapper;
